import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../helpers/AuthContext';

export function useLinks() {
    const { fetch, API_URL } = useAuth();
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // `silent` re-reads without flipping `loading`, so a manager that swaps in a loading state
    // does not unmount the importer modal that triggered the refresh.
    const reload = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setLoadError(null);
        try {
            const res = await fetch(`${API_URL}/v1/seller/links`);
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Failed to load links');
            setLinks(json.data || []);
        } catch (e) {
            setLoadError(e.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [fetch, API_URL]);

    useEffect(() => { reload(); }, [reload]);

    async function request(url, method, payload) {
        try {
            const res = await fetch(`${API_URL}${url}`, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: payload !== undefined ? JSON.stringify(payload) : undefined,
            });
            const json = await res.json();
            if (!res.ok) return { success: false, error: json.error || 'Request failed' };
            return { success: true, data: json.data };
        } catch (e) {
            return { success: false, error: e.message };
        }
    }

    async function createLink(payload) {
        const result = await request('/v1/seller/links', 'POST', payload);
        if (result.success) {
            setLinks(prev => [...prev, result.data]);
        }
        return result;
    }

    async function updateLink(id, payload) {
        const result = await request(`/v1/seller/links/${id}`, 'PATCH', payload);
        if (result.success) {
            setLinks(prev => prev.map(l => l.id === id ? result.data : l));
        }
        return result;
    }

    async function deleteLink(id) {
        const result = await request(`/v1/seller/links/${id}`, 'DELETE');
        if (result.success) {
            setLinks(result.data);
        }
        return result;
    }

    async function moveLink(id, direction) {
        const result = await request(`/v1/seller/links/${id}/move`, 'POST', { direction });
        if (result.success) {
            setLinks(result.data);
        }
        return result;
    }

    return { links, loading, loadError, reload, createLink, updateLink, deleteLink, moveLink };
}
